import type { AnswerContactRequest, ChannelListResponse, ChannelThread, ChannelThreadListResponse, ChannelThreadResponse, ChannelView, ContactChatEntry, StartChannelLinkRequest, UpdateChannelSettingsRequest, UpdateChannelThreadRequest, TodoListResponse } from "@ark-bot/protocol";
import type { LoginChatEntry, SubmitLoginRequest } from "@ark-bot/protocol";
import type { Agent, ApprovalResponse, ChatResponse, CompactResponse, ConnectPluginResponse, CreateAgentRequest, PluginConnectionList, PluginToolkitPage, PluginsStatus, CreateRoutineRequest, CreateSkillRequest, HandoffResolution, HandoffResponse, PostMessageRequest, RosterEvent, Routine, RoutineListResponse, ServerEvent, Settings, Skill, SkillListResponse, UpdateAgentRequest, UpdateRoutineRequest, UpdateSettingsRequest, UpdateSkillRequest, MemoryListResponse, WorkspaceSkillListResponse } from "@ark-bot/protocol";

import { apiBase, configuredHost } from "./host.js";
import { serverText } from "./text/server.js";

/**
 * The web app talks to the host UI server at /api, which adds the bearer and
 * forwards to the box gateway; the token never reaches the browser. A page
 * the host serves finds /api on its own origin; the packaged app finds it
 * under the host it was given (host.ts), read once here.
 */
const BASE = apiBase(configuredHost());

async function json<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) throw new Error(body.error != null ? serverText(body.error) : `${response.status} ${response.statusText}`);
  return body;
}

/** One of the box's screens: where its WebSocket is and how big it is. */
export interface VncScreen {
  /** As the box knows it, e.g. ":1"; the same name an agent's `screens` list carries. */
  readonly display: string;
  readonly socketPath: string;
  readonly width: number;
  readonly height: number;
}

/** GET /api/vnc: every screen the box serves, and what RFB needs to open one. */
export interface VncConfig {
  readonly password: string;
  readonly screens: readonly VncScreen[];
}

/** A model a bot may run: its id as a bot's `mainModel` carries it, the name users see, and its price in credits per million tokens. */
export interface ModelOption {
  readonly id: string;
  readonly label: string;
  /** Fresh input, cached input and output, as a request is charged; absent on a host with no prices (the local model relay). */
  readonly input?: number;
  readonly cached?: number;
  readonly output?: number;
}

/** GET /api/models: what the bot settings offer, and what a bot runs until one is picked. */
export interface ModelChoices {
  readonly models: readonly ModelOption[];
  readonly default: { readonly id: string; readonly label: string } | null;
}

export const api = {
  /**
   * The models a bot may run, from the cloud app, whose operator picks them.
   * `null` on a host that offers no choice (a local box runs what its host
   * configured): the settings then show no picker, as they do for an empty list.
   */
  models: async (): Promise<ModelChoices | null> => {
    const response = await fetch(`${BASE}/models`).catch(() => null);
    if (response == null || !response.ok) return null;
    const body = (await response.json().catch(() => null)) as ModelChoices | null;
    return body != null && Array.isArray(body.models) ? body : null;
  },
  /**
   * `null` when this host serves no screen: either it is a lite box, which
   * has no desktop at all (§6.17), or a full one started without one. The
   * app leaves the screen out entirely rather than showing a pane that could
   * never connect.
   */
  vnc: async (): Promise<VncConfig | null> => {
    const response = await fetch(`${BASE}/vnc`);
    if (response.status === 404) return null;
    return json<VncConfig>(response);
  },
  listAgents: (): Promise<Agent[]> => fetch(`${BASE}/agents`).then((r) => json<Agent[]>(r)),
  /** A new agent, with its profile if one was written; `introduce` has it open the conversation itself, as in the prototype. */
  createAgent: (request: CreateAgentRequest): Promise<Agent> =>
    fetch(`${BASE}/agents`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Agent>(r)),
  updateAgent: (id: string, request: UpdateAgentRequest): Promise<Agent> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Agent>(r)),
  deleteAgent: (id: string): Promise<void> => fetch(`${BASE}/agents/${encodeURIComponent(id)}`, { method: "DELETE" }).then((r) => json(r)),
  /** The newest `limit` bubbles (below `before`, for the page above the ones shown), with the event cursor to open the stream from. */
  chat: (id: string, page: { readonly limit?: number; readonly before?: number } = {}): Promise<ChatResponse> => {
    const query = new URLSearchParams();
    if (page.limit != null) query.set("limit", String(page.limit));
    if (page.before != null) query.set("before", String(page.before));
    const suffix = query.size === 0 ? "" : `?${query.toString()}`;
    return fetch(`${BASE}/agents/${encodeURIComponent(id)}/chat${suffix}`).then((r) => json<ChatResponse>(r));
  },
  /** Condenses the agent's conversation now rather than when the window fills; 409 while it is working. */
  compact: (id: string): Promise<CompactResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/compact`, { method: "POST" }).then((r) => json<CompactResponse>(r)),
  /** A copy of the agent: profile, routines, skills and memory, but not the chat. */
  duplicateAgent: (id: string): Promise<Agent> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/duplicate`, { method: "POST" }).then((r) => json<Agent>(r)),
  postMessage: (id: string, request: PostMessageRequest): Promise<{ turnId: string }> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/messages`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json(r)),
  /** Answers an approval card, optionally adding a rule to the always-allow list first. A card no longer pending answers 409 with its state. */
  resolveApproval: (approvalId: string, approved: boolean, allowRule?: string): Promise<ApprovalResponse> =>
    fetch(`${BASE}/approvals/${encodeURIComponent(approvalId)}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(allowRule == null ? { approved } : { approved, allowRule }) }).then((r) => json<ApprovalResponse>(r)),
  /** The user answering the agent's request for the box: done, or skipped. */
  resolveHandoff: (id: string, resolution: HandoffResolution): Promise<HandoffResponse> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/handoff`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ resolution }) }).then((r) => json<HandoffResponse>(r)),
  /** The user answering a form the agent raised, or dismissing it. */
  submitForm: (id: string, formId: string, values: Record<string, string>): Promise<unknown> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/forms/${encodeURIComponent(formId)}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ values }) }).then((r) => json(r)),
  submitLogin: (id: string, loginId: string, request: SubmitLoginRequest): Promise<LoginChatEntry> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/logins/${encodeURIComponent(loginId)}`, { method: "POST", headers: { "content-type": "application/json" }, cache: "no-store", body: JSON.stringify(request) }).then((r) => json<LoginChatEntry>(r)),
  cancelForm: (id: string, formId: string): Promise<unknown> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/forms/${encodeURIComponent(formId)}`, { method: "POST", headers: { "content-type": "application/json" }, body: '{"cancelled":true}' }).then((r) => json(r)),
  /** An agent's standing orders, soonest first, with the zone their schedules are read in. */
  routines: (id: string): Promise<RoutineListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/routines`).then((r) => json<RoutineListResponse>(r)),
  createRoutine: (id: string, request: CreateRoutineRequest): Promise<Routine> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/routines`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Routine>(r)),
  updateRoutine: (id: string, routineId: string, request: UpdateRoutineRequest): Promise<Routine> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/routines/${encodeURIComponent(routineId)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Routine>(r)),
  deleteRoutine: (id: string, routineId: string): Promise<void> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/routines/${encodeURIComponent(routineId)}`, { method: "DELETE" }).then((r) => json(r)),
  /** Run now: the same turn the schedule would have opened. */
  runRoutine: (id: string, routineId: string): Promise<{ runId: string; turnId: string }> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/routines/${encodeURIComponent(routineId)}/run`, { method: "POST" }).then((r) => json(r)),
  /** The agent's written procedures, and the folder they live in. */
  skills: (id: string): Promise<SkillListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/skills`).then((r) => json<SkillListResponse>(r)),
  /** Shared skills and every bot's own, for Settings. */
  allSkills: (): Promise<WorkspaceSkillListResponse> => fetch(`${BASE}/skills`).then((r) => json<WorkspaceSkillListResponse>(r)),
  memory: (id: string): Promise<MemoryListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory`).then((r) => json<MemoryListResponse>(r)),
  forgetMemory: (id: string, factId: string): Promise<void> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory/${encodeURIComponent(factId)}`, { method: "DELETE" }).then((r) => json(r)),
  tidyMemory: (id: string): Promise<{ tidied: boolean; summary?: string }> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory/tidy`, { method: "POST" }).then((r) => json<{ tidied: boolean; summary?: string }>(r)),
  restoreMemory: (id: string, factId: string): Promise<void> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory/${encodeURIComponent(factId)}/restore`, { method: "POST" }).then((r) => json(r)),
  undoMemoryTidy: (id: string): Promise<void> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory/undo`, { method: "POST" }).then((r) => json(r)),
  clearMemory: (id: string): Promise<void> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/memory`, { method: "DELETE" }).then((r) => json(r)),
  todos: (id: string): Promise<TodoListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/todos`).then((r) => json<TodoListResponse>(r)),
  createSkill: (id: string, request: CreateSkillRequest): Promise<Skill> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/skills`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Skill>(r)),
  updateSkill: (id: string, skillId: string, request: UpdateSkillRequest): Promise<Skill> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/skills/${encodeURIComponent(skillId)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Skill>(r)),
  deleteSkill: (id: string, skillId: string): Promise<void> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/skills/${encodeURIComponent(skillId)}`, { method: "DELETE" }).then((r) => json(r)),
  createSharedSkill: (request: CreateSkillRequest): Promise<Skill> =>
    fetch(`${BASE}/skills/shared`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Skill>(r)),
  updateSharedSkill: (skillId: string, request: UpdateSkillRequest): Promise<Skill> =>
    fetch(`${BASE}/skills/shared/${encodeURIComponent(skillId)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<Skill>(r)),
  deleteSharedSkill: (skillId: string): Promise<void> => fetch(`${BASE}/skills/shared/${encodeURIComponent(skillId)}`, { method: "DELETE" }).then((r) => json(r)),
  settings: (): Promise<Settings> => fetch(`${BASE}/settings`).then((r) => json<Settings>(r)),
  updateSettings: (update: UpdateSettingsRequest): Promise<Settings> =>
    fetch(`${BASE}/settings`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(update) }).then((r) => json<Settings>(r)),
  /** Plugins (§6.18): whether the box has Composio at all, its catalogue, and the user's connections to it. */
  pluginsStatus: (): Promise<PluginsStatus> => fetch(`${BASE}/plugins`).then((r) => json<PluginsStatus>(r)),
  pluginToolkits: (search: string, cursor?: string | null): Promise<PluginToolkitPage> => {
    const query = new URLSearchParams();
    if (search.trim().length > 0) query.set("search", search.trim());
    if (cursor != null) query.set("cursor", cursor);
    return fetch(`${BASE}/plugins/toolkits${query.size === 0 ? "" : `?${query.toString()}`}`).then((r) => json<PluginToolkitPage>(r));
  },
  pluginConnections: (): Promise<PluginConnectionList> => fetch(`${BASE}/plugins/connections`).then((r) => json<PluginConnectionList>(r)),
  /** A sign-in link for the app; the connection is pending until the user finishes there. */
  connectPlugin: (toolkit: string): Promise<ConnectPluginResponse> =>
    fetch(`${BASE}/plugins/connections`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ toolkit }) }).then((r) => json<ConnectPluginResponse>(r)),
  disconnectPlugin: (connectionId: string): Promise<unknown> => fetch(`${BASE}/plugins/connections/${encodeURIComponent(connectionId)}`, { method: "DELETE" }).then((r) => json(r)),
  uploadBlob: async (file: File): Promise<{ blobId: string; mimeType: string; size: number }> =>
    fetch(`${BASE}/blobs`, { method: "POST", headers: { "content-type": file.type || "application/octet-stream" }, body: file }).then((r) => json(r)),
  blobUrl: (blobId: string): string => `${BASE}/blobs/${blobId}`,
  /** The packaged app's FCM token, kept by the box so the host beside it can push (client-app/ANDROID.md §5); the same token again is a refresh. */
  registerPush: (token: string, device: string): Promise<{ registeredAt: number }> =>
    fetch(`${BASE}/notifications/subscriptions`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ platform: "fcm", token, device }) }).then((r) => json(r)),
  unregisterPush: (token: string): Promise<unknown> => fetch(`${BASE}/notifications/subscriptions/${encodeURIComponent(token)}`, { method: "DELETE" }).then((r) => json(r)),

  // Channels (§6.19): the agent's WhatsApp number and the people who write to it.
  channels: (id: string): Promise<ChannelListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels`).then((r) => json<ChannelListResponse>(r)),
  linkChannel: (id: string, request: StartChannelLinkRequest): Promise<ChannelView> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/link`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<ChannelView>(r)),
  refreshChannel: (id: string): Promise<ChannelView> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/refresh`, { method: "POST" }).then((r) => json<ChannelView>(r)),
  unlinkChannel: (id: string): Promise<ChannelView> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp`, { method: "DELETE" }).then((r) => json<ChannelView>(r)),
  updateChannelSettings: (id: string, update: UpdateChannelSettingsRequest): Promise<ChannelView> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/settings`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(update) }).then((r) => json<ChannelView>(r)),
  channelThreads: (id: string): Promise<ChannelThreadListResponse> => fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/threads`).then((r) => json<ChannelThreadListResponse>(r)),
  channelThread: (id: string, threadId: string): Promise<ChannelThreadResponse> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/threads/${encodeURIComponent(threadId)}`).then((r) => json<ChannelThreadResponse>(r)),
  updateChannelThread: (id: string, threadId: string, request: UpdateChannelThreadRequest): Promise<ChannelThread> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/channels/whatsapp/threads/${encodeURIComponent(threadId)}`, { method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<ChannelThread>(r)),
  answerContact: (id: string, entryId: string, request: AnswerContactRequest): Promise<ContactChatEntry> =>
    fetch(`${BASE}/agents/${encodeURIComponent(id)}/contacts/${encodeURIComponent(entryId)}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(request) }).then((r) => json<ContactChatEntry>(r)),
};

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface Subscription {
  close(): void;
}

const EVENT_TYPES: ReadonlyArray<ServerEvent["type"]> = [
  "turn.started",
  "user.message",
  "chat.entry",
  "chat.entry.updated",
  "prompt.withdrawn",
  "text.delta",
  "reasoning.delta",
  "tool.started",
  "tool.progress",
  "tool.finished",
  "session.spawned",
  "session.finished",
  "approval.requested",
  "approval.resolved",
  "context.condensed",
  "room.speaker",
  "routines.changed",
  "screens",
  "skills.changed",
  "channel.changed",
  "channel.thread",
  "turn.finished",
  "error",
];

/** How long before the page tries the box again, doubling while it stays away. */
export const RETRY_MS = 1_000;
export const MAX_RETRY_MS = 15_000;
/** Silence that means the stream is dead: three of the gateway's 15s heartbeats. */
export const STALL_MS = 45_000;

interface StreamOptions {
  /** Where to open, asked again on every reopen so a cursor can move. */
  readonly url: () => string;
  /** The named events to deliver. */
  readonly types: readonly string[];
  readonly onMessage: (message: MessageEvent<string>) => void;
  readonly onStatus: (status: ConnectionStatus) => void;
}

/**
 * One SSE stream that stays alive. EventSource reconnects on its own and
 * sends Last-Event-ID, which the gateway honours, so a dropped connection
 * resumes where it stopped.
 *
 * Two ways that is not enough, both of which leave the page looking perfectly
 * alive with a dead stream behind it — messages send, turns run, and nothing
 * arrives until the agent is switched:
 *
 * - The browser gives up for good on what it reads as a fatal error: the
 *   host's own 502 while the box restarts, a refused port, a wrong content
 *   type. So a closed stream is opened again here, with a widening wait.
 * - Nothing fails at all. The box's runtime restarts, the host holds the
 *   browser's connection open, and no error is ever raised. Silence is the
 *   only symptom, so the gateway's heartbeat is what proves the stream alive,
 *   and going quiet is what reopens it.
 */
function openStream({ url, types, onMessage, onStatus }: StreamOptions): Subscription {
  // A closed subscription must fall silent: React's strict mode opens and closes one
  // on mount, and its abort would otherwise report "reconnecting" over the live one's "open".
  let closed = false;
  let wait = RETRY_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  let source: EventSource;

  /** Restarted by everything the stream delivers, heartbeats included. */
  const expectTraffic = (): void => {
    if (watchdog != null) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (closed) return;
      source.close();
      onStatus("reconnecting");
      open();
    }, STALL_MS);
  };

  const open = (): void => {
    source = new EventSource(url());
    const current = source;
    onStatus("connecting");
    expectTraffic();
    current.onopen = () => {
      if (!closed) onStatus("open");
    };
    current.onerror = (event) => {
      // Our "error" server event arrives here too (the SSE event name is "error");
      // it carries data and is handled by the listener below, unlike a transport error.
      if (closed || "data" in event) return;
      if (current.readyState !== EventSource.CLOSED) {
        onStatus("reconnecting");
        return;
      }
      // The browser has stopped; from here the retrying is ours.
      onStatus("reconnecting");
      timer = setTimeout(open, wait);
      wait = Math.min(wait * 2, MAX_RETRY_MS);
    };
    const handle = (message: MessageEvent<string>): void => {
      if (closed) return;
      expectTraffic();
      // A stream that delivered is a stream that worked: the next gap waits from the start again.
      wait = RETRY_MS;
      onMessage(message);
    };
    for (const type of types) current.addEventListener(type, handle as EventListener);
    // The heartbeat says only that the box is still there; it carries no seq and no event.
    current.addEventListener("ping", () => {
      if (!closed) expectTraffic();
    });
    // The server said this stream will never carry anything more: the agent is gone.
    current.addEventListener("gone", () => {
      if (!closed) onStatus("closed");
      closed = true;
      if (watchdog != null) clearTimeout(watchdog);
      current.close();
    });
  };

  open();
  return {
    close: () => {
      closed = true;
      if (timer != null) clearTimeout(timer);
      if (watchdog != null) clearTimeout(watchdog);
      source.close();
    },
  };
}

/** Subscribes to an agent's event log from `after`; a reopen resumes from the last seq delivered. */
export function subscribe(agentId: string, after: number, onEvent: (seq: number, event: ServerEvent) => void, onStatus: (status: ConnectionStatus) => void): Subscription {
  let from = after;
  return openStream({
    url: () => `${BASE}/stream?agentId=${encodeURIComponent(agentId)}&after=${from}`,
    types: EVENT_TYPES,
    onStatus,
    onMessage: (message) => {
      const seq = Number(message.lastEventId);
      if (!Number.isFinite(seq)) return;
      from = seq;
      onEvent(seq, JSON.parse(message.data) as ServerEvent);
    },
  });
}

const ROSTER_EVENT_TYPES: ReadonlyArray<RosterEvent["type"]> = ["roster", "agent", "agent.deleted"];

/**
 * The sidebar's stream (ARCHITECTURE §5.5): the whole roster on every open,
 * then each agent's row as its chat moves. No cursor, so a reopen simply
 * starts again with the list.
 */
export function subscribeRoster(onEvent: (event: RosterEvent) => void, onStatus: (status: ConnectionStatus) => void): Subscription {
  return openStream({
    url: () => `${BASE}/roster`,
    types: ROSTER_EVENT_TYPES,
    onStatus,
    onMessage: (message) => onEvent(JSON.parse(message.data) as RosterEvent),
  });
}
